import {
  TypeGeneralLinkSkeleton,
  TypeNavGroupHeadingSkeleton,
  TypeNavLinkListFields,
  TypeNavLinkListSkeleton,
} from "@customTypes/contentful";
import clsx from "clsx";
import { useNavigationStateContext } from "context/navigationContext";
import { FC, useEffect, useRef } from "react";
import styles from "./MegaNav.module.scss";
import NavLink from "./NavLink";

type Props = {
  items: (
    | TypeNavLinkListSkeleton
    | TypeGeneralLinkSkeleton
    | TypeNavGroupHeadingSkeleton
  )[];
  menuActive: boolean;
  level?: number;
  parentIndex?: number;
};

const MAX_COUNT = 14;

const NavLinkList: FC<Props> = ({ items, menuActive, level = 1 }) => {
  const { setOpenSubmenu, isSubmenuOpen, navHeight, setNavHeight } =
    useNavigationStateContext();
  const navListRef = useRef<HTMLUListElement>(null);

  const isExpanded = menuActive;
  const noPromoVisible = items.length > MAX_COUNT && level === 3;

  useEffect(() => {
    if (isExpanded && navListRef.current && !noPromoVisible) {
      const currentHeight = navListRef.current.offsetHeight;

      if (currentHeight > navHeight) {
        setNavHeight(currentHeight);
      }
    }
  }, [isExpanded, items, navHeight, setNavHeight, noPromoVisible]);

  if (!items || items.length === 0 || level > 3) return null;

  return (
    <ul
      className={clsx(
        styles[`level-${level}`],
        styles.navList,
        isExpanded && styles.open,
        noPromoVisible && styles.noPromo
      )}
      ref={navListRef}
      style={{ minHeight: `${navHeight}px` }}
    >
      {items.map((item, index) => {
        if (!item?.fields) return null;
        const { fields } = item;
        const { linkList } = fields as TypeNavLinkListFields;

        // Check if submenu at this level and index is open
        const submenuOpen = isSubmenuOpen(level, index);

        // Handler to open submenu on click
        const handleEnter = () => {
          setOpenSubmenu(level, index);
        };

        return (
          <NavLink
            navLink={item}
            key={index}
            onClick={handleEnter}
            onMouseEnter={handleEnter}
            active={submenuOpen}
          >
            {linkList?.length > 0 && (
              <NavLinkList
                items={linkList}
                level={level + 1}
                menuActive={submenuOpen}
              />
            )}
          </NavLink>
        );
      })}
    </ul>
  );
};

export default NavLinkList;
